// Client-side mirror of the campaign API's mission shapes
// (apps/wars-server/src/missions/missions.service.ts). Previously these were
// re-declared inline in campaign.page.tsx, campaign/[id].tsx and
// my-games.page.tsx, which is how the three drifted apart; keep them here so a
// server-side field change is a one-place edit on this side too.

export interface GamePreview {
  terrainMap: { width: number; height: number; data: Record<string, string> };
  ownerOverrides: Record<string, number>;
}

export type MissionDifficulty =
  | 'very-easy'
  | 'easy'
  | 'medium'
  | 'hard'
  | 'very-hard';

// Presentation-ready labels for the difficulty ramp. The server sends the
// slug; the display string is a client concern.
export const DIFFICULTY_LABEL: Record<MissionDifficulty, string> = {
  'very-easy': 'Very easy',
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  'very-hard': 'Very hard',
};

export interface MissionCommander {
  id: string;
  name: string;
  title: string;
  faction: string;
  flavor: string;
  description: string[];
}

export interface MissionBriefing {
  situation: string;
  objectives: string[];
  teaches: string[];
}

export interface MissionGame {
  id: number;
  active: boolean;
  currentTurn: number;
  wonBy: number[];
  slots: Array<{
    slotId: number;
    user: { id: string; username: string } | null;
  }>;
  preview: GamePreview | null;
}

export interface MissionListEntry {
  id: string;
  name: string;
  act: 1 | 2 | 3 | 4;
  orderInAct: number;
  kind: 'personal' | 'pick' | 'finale';
  difficulty: MissionDifficulty;
  commander: MissionCommander;
  briefing?: MissionBriefing;
  parTurns?: number;
  locked: boolean;
  completed: boolean;
  fogOfWar: boolean;
  terrainMap: { width: number; height: number; data: Record<string, string> };
  activeGame: { id: number; preview: GamePreview | null } | null;
}

// The detail endpoint returns the same entry plus this user's game history for
// the mission, and no activeGame (it's derivable from `games`).
export type MissionDetail = Omit<MissionListEntry, 'activeGame'> & {
  games: MissionGame[];
};
